import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import * as Yup from "yup";
import { Form, Formik, FormikProps, useFormikContext } from "formik";
import toast from "react-hot-toast";
import { Box, Card, Flex, Text } from "@radix-ui/themes";
import {
  WorkflowStep,
  WorkflowAction,
  FilterCondition,
} from "@/types/breeze/Workflow";
import { isInternalHostname } from "@/utils/hostname";
import { slugify } from "@/utils/text";
import InputField from "@/components/form/InputField";
import ReadDataFields, {
  readDataValidationSchema,
} from "@/components/workflow/fields/ReadDataFields";
import CreateDataFields, {
  createDataValidationSchema,
} from "@/components/workflow/fields/CreateDataFields";
import UpdateDataFields, {
  updateDataValidationSchema,
} from "@/components/workflow/fields/UpdateDataFields";
import DeleteDataFields, {
  deleteDataValidationSchema,
} from "@/components/workflow/fields/DeleteDataFields";
import CallAnApiFields, {
  callAnApiValidationSchema,
} from "@/components/workflow/fields/CallAnApiFields";
import RunScriptFields, {
  runScriptValidationSchema,
} from "./fields/RunScriptFields";
import CheckboxField from "../form/CheckboxField";
import SendACommandFields, {
  sendACommandValidationSchema,
} from "./fields/SendACommandFields";
import ConditionFields, {
  conditionValidationSchema,
} from "./fields/ConditionFields";
import SendPushNotificationFields, {
  sendPushNotificationValidationSchema,
} from "./fields/SendPushNotificationFields";
import SendAnEmailFields, {
  sendAnEmailValidationSchema,
  runEmailTemplatePreSave,
} from "./fields/SendAnEmailFields";
import IntegrationFields, {
  integrationValidationSchema,
} from "./fields/IntegrationFields";
import FileHandlerFields, {
  fileHandlerValidationSchema,
} from "./fields/FileHandlerFields";
import UpsertDataFields, {
  upsertDataValidationSchema,
} from "./fields/UpsertDataFields";
import AdjustBalanceFields, {
  adjustBalanceValidationSchema,
} from "./fields/AdjustBalanceFields";
import ActionSelector from "./ActionSelector";
import { useAuth } from "@/providers/AuthProvider";
import { useQuery } from "@tanstack/react-query";
import { integrationService } from "@/services/IntegrationService";

// Define a type for the form values
type StepFormValues = {
  key: string;
  name: string;
  description: string;
  enabled: boolean;
  action: WorkflowAction;
  parameters: {
    [key: string]:
      | string
      | number
      | boolean
      | null
      | Record<string, unknown>
      | FilterCondition[];
  };
};

export type StepFormHandle = {
  saveChanges: (cb?: () => void) => void;
  cancelChanges: (cb?: () => void) => void;
};

function stripBlankAttachments(parameters: any): any {
  if (!parameters || typeof parameters !== "object") return parameters;
  const attachments = parameters.attachments;
  if (!Array.isArray(attachments)) return parameters;
  const cleaned = attachments.filter(
    (a) => a && a.file_id !== undefined && a.file_id !== null && a.file_id !== "",
  );
  if (cleaned.length === attachments.length) return parameters;
  return { ...parameters, attachments: cleaned };
}

function buildTouched(errors: any): any {
  if (!errors || typeof errors !== "object") return true;
  if (Array.isArray(errors)) return errors.map((e) => buildTouched(e));
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(errors)) {
    out[k] = typeof v === "object" && v !== null ? buildTouched(v) : true;
  }
  return out;
}

function firstErrorMessage(errors: any): string | null {
  if (!errors) return null;
  if (typeof errors === "string") return errors;
  if (Array.isArray(errors)) {
    for (const e of errors) {
      const m = firstErrorMessage(e);
      if (m) return m;
    }
    return null;
  }
  if (typeof errors === "object") {
    for (const v of Object.values(errors)) {
      const m = firstErrorMessage(v);
      if (m) return m;
    }
  }
  return null;
}

// Base validation schema for all steps
const baseStepSchema = {
  key: Yup.string()
    .matches(
      /^[a-zA-Z0-9_]+$/,
      "Key can only contain alphanumeric characters and underscores",
    )
    .required("Key is required"),
  name: Yup.string().required("Name is required"),
  description: Yup.string(),
  enabled: Yup.boolean(),
  action: Yup.string().required("Action is required"),
};

function FormikSubmitErrorWatcher() {
  const { submitCount, errors, isSubmitting, setTouched } = useFormikContext<any>();
  const lastSeen = useRef(0);

  useEffect(() => {
    if (submitCount === lastSeen.current) return;
    lastSeen.current = submitCount;
    if (isSubmitting) return;
    if (!errors || Object.keys(errors).length === 0) return;
    setTouched(buildTouched(errors), false);
    const firstMessage = firstErrorMessage(errors);
    if (firstMessage) toast.error(firstMessage);
    // eslint-disable-next-line no-console
    console.warn("[StepForm] validation errors", errors);
  }, [submitCount, errors, isSubmitting, setTouched]);

  return null;
}

function FormikSubmitBridge({
  formRef,
  onReady,
}: {
  formRef: React.RefObject<FormikProps<any>>;
  onReady?: (api: { submit: () => Promise<void> }) => void;
}) {
  useEffect(() => {
    if (!onReady) return;
    onReady({
      submit: async () => {
        if (!formRef.current) return;
        await formRef.current.submitForm();
        const errors = formRef.current.errors;
        if (errors && Object.keys(errors).length > 0) {
          formRef.current.setTouched(buildTouched(errors), false);
          const firstMessage = firstErrorMessage(errors);
          if (firstMessage) toast.error(firstMessage);
        }
      },
    });
  }, [onReady, formRef]);
  return null;
}

// Keeps key in sync with name until the user diverges the key manually.
// touched.key isn't reliable — Formik marks it on submit-attempt too.
function KeyAutoGenerator() {
  const { values, setFieldValue } = useFormikContext<StepFormValues>();
  const lastAuto = useRef("");

  useEffect(() => {
    if (!values.name) return;
    const next = slugify(values.name);
    if (values.key && values.key !== lastAuto.current) return;
    lastAuto.current = next;
    if (values.key !== next) setFieldValue("key", next);
  }, [values.name, values.key, setFieldValue]);

  return null;
}

// Resolves the integration definition from the selected provider and renders IntegrationFields
function IntegrationFieldsWrapper({
  existingSteps,
  triggerEntity,
}: {
  existingSteps: WorkflowStep[];
  triggerEntity?: string;
}) {
  const { values } = useFormikContext<StepFormValues>();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id || 0;
  const provider = values.parameters?.provider as string;

  const { data: definitions = [] } = useQuery({
    queryKey: ["integration-definitions", tenantId],
    queryFn: () => integrationService.getDefinitions(),
    enabled: !!tenantId,
  });

  const definition = definitions.find((d) => d.provider === provider);

  if (!provider) {
    return (
      <Text size="2" color="gray">
        Select an integration from the Integrations tab above
      </Text>
    );
  }

  if (!definition) {
    return (
      <Text size="2" color="gray">
        Loading integration...
      </Text>
    );
  }

  return <IntegrationFields definition={definition} existingSteps={existingSteps} triggerEntity={triggerEntity} />;
}

interface StepFormProps {
  existingSteps: WorkflowStep[];
  triggerEntity?: string;
  step?: WorkflowStep;
  onClose: () => void;
  onStepCreated?: (step: WorkflowStep) => void;
  onStepUpdated?: (step: WorkflowStep) => void;
  onReady?: (api: { submit: () => Promise<void> }) => void;
}

const StepForm = forwardRef<StepFormHandle, StepFormProps>(
  ({ existingSteps, triggerEntity, step, onClose, onStepCreated, onStepUpdated, onReady }, ref) => {
    const formRef = useRef<FormikProps<StepFormValues>>(null);

    useImperativeHandle(ref, () => ({
      saveChanges: (cb?: () => void) => {
        formRef.current?.submitForm().then(() => {
          cb?.();
        });
      },
      cancelChanges: (cb?: () => void) => {
        formRef.current?.resetForm();
        onClose();
        cb?.();
      },
    }));

    const [isBrowsing, setIsBrowsing] = useState(!step);
    const isEditMode = !!step;
    const [validationSchema, setValidationSchema] =
      useState<Yup.ObjectSchema<any>>();

    const getStepValidationSchema = (action: WorkflowAction) => {
      let baseSchema = Yup.object().shape(baseStepSchema);

      switch (action) {
        case WorkflowAction.ReadData:
          baseSchema = baseSchema.concat(readDataValidationSchema);
          break;
        case WorkflowAction.CreateData:
          baseSchema = baseSchema.concat(createDataValidationSchema);
          break;
        case WorkflowAction.UpdateData:
          baseSchema = baseSchema.concat(updateDataValidationSchema);
          break;
        case WorkflowAction.DeleteData:
          baseSchema = baseSchema.concat(deleteDataValidationSchema);
          break;
        case WorkflowAction.CallAnApi:
          baseSchema = baseSchema.concat(callAnApiValidationSchema);
          break;
        case WorkflowAction.RunScript:
          baseSchema = baseSchema.concat(runScriptValidationSchema);
          break;
        case WorkflowAction.SendACommand:
          // Only add validation schema if hostname is allowed
          if (isInternalHostname()) {
            baseSchema = baseSchema.concat(sendACommandValidationSchema);
          }
          break;
        case WorkflowAction.Condition:
          baseSchema = baseSchema.concat(conditionValidationSchema);
          break;
        case WorkflowAction.SendAnEmail:
          baseSchema = baseSchema.concat(sendAnEmailValidationSchema);
          break;
        case WorkflowAction.SendPushNotification:
          baseSchema = baseSchema.concat(sendPushNotificationValidationSchema);
          break;
        case WorkflowAction.AdjustBalance:
          baseSchema = baseSchema.concat(adjustBalanceValidationSchema);
          break;
        case WorkflowAction.Integration:
          baseSchema = baseSchema.concat(integrationValidationSchema);
          break;
        case WorkflowAction.FileHandler:
          baseSchema = baseSchema.concat(fileHandlerValidationSchema);
          break;
        case WorkflowAction.UpsertData:
          baseSchema = baseSchema.concat(upsertDataValidationSchema);
          break;
      }

      setValidationSchema(baseSchema);
    };

    // Set initial values based on whether we're editing or creating
    const initialValues: StepFormValues = isEditMode
      ? {
          key: step.key,
          name: step.name,
          description: step.description || "",
          enabled: step.enabled,
          action: step.action,
          parameters: step.parameters || {},
        }
      : {
          key: "",
          name: "",
          description: "",
          enabled: true,
          action: WorkflowAction.None,
          parameters: {},
        };

    useEffect(() => {
      getStepValidationSchema(initialValues.action);
    }, [initialValues.action]);

    const renderActionFields = (values: StepFormValues) => {
      switch (values.action) {
        case WorkflowAction.ReadData:
          return <ReadDataFields />;
        case WorkflowAction.CreateData:
          return <CreateDataFields existingSteps={existingSteps} triggerEntity={triggerEntity} />;
        case WorkflowAction.UpdateData:
          return <UpdateDataFields existingSteps={existingSteps} triggerEntity={triggerEntity} />;
        case WorkflowAction.DeleteData:
          return <DeleteDataFields />;
        case WorkflowAction.CallAnApi:
          return <CallAnApiFields />;
        case WorkflowAction.RunScript:
          return <RunScriptFields />;
        case WorkflowAction.SendACommand:
          if (!isInternalHostname()) {
            return (
              <Text size="2" color="red">
                SendACommand is only available on internal environments
              </Text>
            );
          }
          return <SendACommandFields />;
        case WorkflowAction.Condition:
          return <ConditionFields />;
        case WorkflowAction.SendAnEmail:
          return <SendAnEmailFields existingSteps={existingSteps} triggerEntity={triggerEntity} />;
        case WorkflowAction.SendPushNotification:
          return <SendPushNotificationFields existingSteps={existingSteps} triggerEntity={triggerEntity} />;
        case WorkflowAction.AdjustBalance:
          return <AdjustBalanceFields existingSteps={existingSteps} triggerEntity={triggerEntity} />;
        case WorkflowAction.Integration:
          return <IntegrationFieldsWrapper existingSteps={existingSteps} triggerEntity={triggerEntity} />;
        case WorkflowAction.FileHandler:
          return <FileHandlerFields />;
        case WorkflowAction.UpsertData:
          return <UpsertDataFields existingSteps={existingSteps} triggerEntity={triggerEntity} />;
        default:
          return (
            <Text size="2" color="gray">
              No parameters required
            </Text>
          );
      }
    };

    const handleSubmit = async (
      values: StepFormValues,
      { setSubmitting }: any,
    ) => {
      if (
        values.action === WorkflowAction.SendACommand &&
        !isInternalHostname()
      ) {
        setSubmitting(false);
        return;
      }

      if (values.action === WorkflowAction.SendAnEmail) {
        try {
          await runEmailTemplatePreSave();
        } catch (err) {
          toast.error("Failed to save template changes");
          console.warn("[StepForm] template pre-save failed", err);
          setSubmitting(false);
          return;
        }
      }

      const cleanedParameters = stripBlankAttachments(values.parameters);

      const newStep: WorkflowStep = {
        id: isEditMode ? step!.id : 0,
        created_at: isEditMode ? step!.created_at : new Date().toISOString(),
        updated_at: new Date().toISOString(),
        locked: false,

        key: values.key,
        name: values.name,
        description: values.description || undefined,
        enabled: values.enabled,
        action: values.action,
        parameters: cleanedParameters,

        is_start_step: isEditMode ? step!.is_start_step : false,
        on_success_step_id: isEditMode ? step!.on_success_step_id : null,
        on_failure_step_id: isEditMode ? step!.on_failure_step_id : null,
      };

      if (isEditMode && onStepUpdated) {
        onStepUpdated(newStep);
      } else if (onStepCreated) {
        onStepCreated(newStep);
      }

      setSubmitting(false);
      onClose();
    };

    return (
      <Box p="2">
        <Formik
          innerRef={formRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnMount={false}
          validateOnChange={true}
          validateOnBlur={true}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form id="step-form">
              <FormikSubmitErrorWatcher />
              <FormikSubmitBridge formRef={formRef} onReady={onReady} />
              <Flex direction="column" gap="2">
                {!isEditMode && <KeyAutoGenerator />}

                <ActionSelector
                  name="action"
                  label="Action"
                  required
                  onChange={(action) => {
                    getStepValidationSchema(action);
                    setFieldValue("action", action);
                  }}
                  onBrowsingChange={setIsBrowsing}
                />

                {/* Hide form fields while browsing actions/integrations picker */}
                {!isBrowsing &&
                 values.action !== WorkflowAction.None &&
                 !(values.action === WorkflowAction.Integration && !values.parameters?.provider) && (
                  <>
                    <Flex gap="2">
                      <InputField name="name" label="Name" required />
                      <InputField
                        name="key"
                        label="Key"
                        required
                        disabled={isEditMode}
                      />
                    </Flex>

                    <InputField
                      name="description"
                      label="Description"
                      as="textarea"
                      options={{ rows: 3 }}
                    />

                    <CheckboxField name="enabled" label="Enable Step" />

                    <Card style={{ backgroundColor: "var(--gray-2)", contain: "none" }}>
                      <Flex direction="column" gap="2" p="2">
                        <Text
                          size="4"
                          weight="medium"
                          color="gray"
                          highContrast
                          mb="2"
                        >
                          Step Parameters
                        </Text>
                        {renderActionFields(values)}
                      </Flex>
                    </Card>
                  </>
                )}
              </Flex>
            </Form>
          )}
        </Formik>
      </Box>
    );
  },
);

export default StepForm;
